import api from '@/api/base';
import type { ApiResponse, CodeRequestModel } from '@/api/types';

// API URLs for code management
const URLS = {
  codes: '/code_request',
  codeById: (id: string) => `/code_request/${id}`,
  uploadCode: '/code_request/upload',
  downloadCode: (id: string) => `/code_request/${id}/download`,
  codeStatus: (id: string) => `/code_request/${id}/status`,
  feedback: '/code_request/feedback',
  commentReset: '/code_request/commentReset',
  stepOne: '/code_request/stepOne',
  incomplete: '/code_request/incomplete',
  facture: '/code_request/facture',
  complete: '/code_request/complete',
} as const;

// Request payloads are loosely typed (the backend accepts partial request
// bodies and various admin action envelopes); responses use the shared
// ApiResponse<CodeRequestModel> envelope.
type CodePayload = Record<string, unknown>;

// Code API methods
const codesApi = {
  // Get all code requests
  getCodes: (token?: string) => {
    return api.get<ApiResponse<CodeRequestModel[]>>(URLS.codes, { token });
  },

  // Get code request by ID
  getCodeById: (id: string, token?: string) => {
    return api.get<ApiResponse<CodeRequestModel>>(URLS.codeById(id), { token });
  },

  // Create new code request
  createCode: (codeData: CodePayload, token?: string) => {
    return api.post<ApiResponse<CodeRequestModel>>(URLS.codes, codeData, {
      token,
    });
  },

  // Update code request
  updateCode: (id: string, codeData: CodePayload, token?: string) => {
    return api.patch<ApiResponse<CodeRequestModel>>(
      URLS.codeById(id),
      codeData,
      { token },
    );
  },

  // Delete code request
  deleteCode: (id: string, token?: string) => {
    return api.delete<ApiResponse<null>>(URLS.codeById(id), { token });
  },

  // Upload code documents
  uploadCode: (formData: FormData, token?: string) => {
    return api.post<ApiResponse<CodeRequestModel>>(URLS.uploadCode, formData, {
      token,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Download code document
  downloadCode: (id: string, token?: string) => {
    return api.get<Blob>(URLS.downloadCode(id), {
      token,
      responseType: 'blob',
    });
  },

  // Update code status
  updateCodeStatus: (id: string, status: unknown, token?: string) => {
    return api.patch<ApiResponse<CodeRequestModel>>(
      URLS.codeStatus(id),
      { status },
      { token },
    );
  },

  // Submit code request feedback
  submitFeedback: (feedbackData: CodePayload, token?: string) => {
    return api.post<ApiResponse<CodeRequestModel>>(
      URLS.feedback,
      feedbackData,
      {
        token,
      },
    );
  },

  // Submit cancellation comment
  submitCancellationComment: (
    codeId: string,
    comment: string,
    token?: string,
  ) => {
    return api.post<ApiResponse<CodeRequestModel>>(
      URLS.commentReset,
      { codeTVNVED_id: codeId, comment },
      { token },
    );
  },

  // Accept code request (step one)
  acceptCode: (codeId: string, token?: string) => {
    return api.patch<ApiResponse<CodeRequestModel>>(
      URLS.stepOne,
      { codeTVNVED_id: codeId },
      { token },
    );
  },

  // Mark code request as incomplete (send back to client)
  markAsIncomplete: (requestData: CodePayload, token?: string) => {
    return api.patch<ApiResponse<CodeRequestModel>>(
      URLS.incomplete,
      requestData,
      { token },
    );
  },

  // Submit facture/invoice file
  submitFacture: (factureData: CodePayload, token?: string) => {
    return api.post<ApiResponse<CodeRequestModel>>(URLS.facture, factureData, {
      token,
    });
  },

  // Complete code request
  completeRequest: (completeData: CodePayload, token?: string) => {
    return api.post<ApiResponse<CodeRequestModel>>(
      URLS.complete,
      completeData,
      {
        token,
      },
    );
  },
};

export default codesApi;
